import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, User, Linkedin } from 'lucide-react';
import { blogData } from './blogData';

// --- SKELETON COMPONENT ---
const PostSkeleton = () => (
  <div className="animate-pulse">
    <div className="w-full h-[400px] md:h-[500px] bg-slate-200" />
    <div className="container mx-auto px-6 mt-12 flex flex-col lg:flex-row gap-12">
      <div className="lg:w-2/3 space-y-4">
        <div className="h-8 bg-slate-200 rounded w-3/4 mb-8" />
        <div className="h-4 bg-slate-100 rounded w-full" />
        <div className="h-4 bg-slate-100 rounded w-full" />
        <div className="h-64 bg-slate-50 rounded w-full mt-8" />
      </div>
      <div className="lg:w-1/3">
        <div className="h-64 bg-slate-100 rounded-2xl" />
      </div>
    </div>
  </div>
);

const handleLinkedinShare = () => {
  const currentUrl = window.location.href; 
  const linkedinShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`;
  window.open(linkedinShareUrl, '_blank', 'width=600,height=600');
};

const BlogPost = () => {
  const { slug } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const blog = blogData.find((b) => b.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => setIsLoading(false), 600);
    return () => clearTimeout(timer);
  }, [slug]);

  if (!blog) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <h2 className="text-2xl font-bold text-slate-800">Blog not found</h2>
        <Link to="/blogs" className="text-blue-600 flex items-center gap-2">
          <ArrowLeft size={20} /> Return to all blogs
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div key="skeleton" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <PostSkeleton />
          </motion.div>
        ) : (
          <motion.div 
            key="content" 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* --- IMPROVED IMAGE FITTING HERO --- */}
            <div className="relative w-full h-[400px] md:h-[500px] overflow-hidden bg-slate-950">
              {/* Blurred Background Layer (Fills the gaps for portrait/small images) */}
              <div 
                className="absolute inset-0 z-0 opacity-30 blur-2xl scale-110"
                style={{ 
                  backgroundImage: `url(${blog.image})`, 
                  backgroundSize: 'cover', 
                  backgroundPosition: 'center' 
                }}
              />

              {/* Main Image Layer */}
              <div className="absolute inset-0 z-10 flex items-center justify-center">
                <img 
                  src={blog.image} 
                  alt={blog.title} 
                  onLoad={() => setImageLoaded(true)}
                  className={`max-h-full max-w-full object-contain transition-opacity duration-700 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`} 
                />
              </div>

              {/* Readability Overlays */}
              <div className="absolute inset-0 z-20 bg-slate-900/40" />
              <div className="absolute inset-0 z-20 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />

              {/* Content Overlay */}
              <div className="absolute inset-0 z-30 flex flex-col justify-end container mx-auto px-6 pb-12">
                <Link to="/blogs" className="text-slate-300 hover:text-white flex items-center gap-2 mb-6 transition-colors w-fit">
                  <ArrowLeft size={20} /> Back to Blogs
                </Link>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white mb-4 max-w-4xl drop-shadow-2xl">
                  {blog.title}
                </h1>
              </div>
            </div>

            {/* Blog Content */}
            <div className="container mx-auto px-6 mt-12 flex flex-col lg:flex-row gap-12">
              <article className="lg:w-2/3">
                <div 
                  className="prose prose-lg prose-slate max-w-none text-slate-700 leading-loose prose-img:rounded-2xl prose-headings:text-slate-900"
                  dangerouslySetInnerHTML={{ __html: blog.content }} 
                />
              </article>

              <aside className="lg:w-1/3">
                <div className="sticky top-28 bg-slate-50 p-8 rounded-2xl border border-slate-100 shadow-sm">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-6">Featured Alumni</h3>
                  
                  <div className="flex items-center gap-4 mb-8">
                    {blog.authorImage ? (
                      <img 
                        src={blog.authorImage} 
                        alt={blog.author} 
                        className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md" 
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-2xl font-bold">
                        {blog.author.charAt(0)}
                      </div>
                    )}
                    
                    <div>
                      <p className="font-bold text-slate-900 text-lg leading-tight">{blog.author}</p>
                      <p className="text-xs text-slate-500 font-medium mt-1">{blog.role}</p>
                    </div>
                  </div>

                  <button 
                    onClick={handleLinkedinShare}
                    className="w-full py-3.5 rounded-xl bg-[#0077b5] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#006097] transition-all shadow-md active:scale-[0.98]"
                  >
                    <Linkedin size={18} /> Share Insights
                  </button>
                </div>
              </aside>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BlogPost;